import { ApplicationUtils } from '../util/ApplicationUtils';
import { default as FetchService } from './FetchService';

export interface ICMSConfig {
	assetPaths?: { images: string };
	published?: string;
	settings?: {
		settings?: { featureFlag_1?: boolean; featureFlag_2?: boolean; someEnumValue?: number };
		sofaConfig?: Record<string, string>;
	};
	version?: string;
}

export interface Config {
	products: [
		{
			type: string;
			id: string;
			sprItems: [
				{
					itemno: string;
				}
			];
		}
	];
	spr: [
		{
			id: string;
			configuration: {
				children: [{ articleId: string }];
			};
		}
	];
	settings?: {
		settings?: {
			coverItems: [{ articleIds: string[] }];
			ymalProducts: [[{ type: string; id: string }]];
			ymalProductsSofaBed: [[{ type: string; id: string }]];
			ymalProductsStorage: [[{ type: string; id: string }]];
			ymalProductsWideArmrest: [[{ type: string; id: string }]];
			ymalProductsLaptopTray: [[{ type: string; id: string }]];
			ymalProductsHeadrest: [[{ type: string; id: string }]];
		};
	};
}

export default class plannerConfigService {
	private cmsConfigVersion: string;
	private endpoint: string;
	private username: string;
	private password: string;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 * @param options.cmsConfigVersion CMS config version.
	 * @param options.endpoint Endpoint.
	 * @param options.username User name.
	 * @param options.password Password.
	 */
	constructor(options: {
		cmsConfigVersion?: string;
		endpoint?: string;
		username?: string;
		password?: string;
	}) {
		this.cmsConfigVersion = options.cmsConfigVersion || '';
		this.endpoint = options.endpoint || '';
		this.username = options.username || '';
		this.password = options.password || '';
	}

	public async getConfig(range: string): Promise<unknown> {
		return this.getConfigData(range);
	}

	public async getCmsConfigData(range: string): Promise<ICMSConfig> {
		const url = ApplicationUtils.isProduction()
			? `${this.endpoint}/addon-app/sofas/content/${this.cmsConfigVersion}/${range}.json`
			: `/addon-app/sofas/content/${this.cmsConfigVersion}/${range}.json`;
		return (await this.fetchJson(url)) as ICMSConfig;
	}

	public parseOutIds(config: Config): Set<string> {
		const data: string[] = [];
		config.products.forEach((product) => {
			data.push(`${product.type}-${product.id}`);
			if (product.sprItems) {
				data.push(...product.sprItems.map((item) => `ART-${item.itemno}`));
			}
		});
		config.spr.forEach((product) => {
			data.push(`SPR-${product.id}`);
			if (product.configuration.children) {
				data.push(...product.configuration.children.map((item) => `ART-${item.articleId}`));
			}
		});
		if (config.settings?.settings?.coverItems) {
			config.settings.settings.coverItems.forEach((coverItem) => {
				data.push(...coverItem.articleIds.map((articleId) => `ART-${articleId}`));
			});
		}
		if (config.settings?.settings?.ymalProducts) {
			Object.values(config.settings.settings.ymalProducts).forEach((ymalProducts) => {
				data.push(...ymalProducts.map((ymal) => `${String(ymal.type).toUpperCase()}-${ymal.id}`));
			});
		}
		if (config.settings?.settings?.ymalProductsSofaBed) {
			Object.values(config.settings.settings.ymalProductsSofaBed).forEach((ymalProducts) => {
				data.push(...ymalProducts.map((ymal) => `${String(ymal.type).toUpperCase()}-${ymal.id}`));
			});
		}
		if (config.settings?.settings?.ymalProductsStorage) {
			Object.values(config.settings.settings.ymalProductsStorage).forEach((ymalProducts) => {
				data.push(...ymalProducts.map((ymal) => `${String(ymal.type).toUpperCase()}-${ymal.id}`));
			});
		}
		if (config.settings?.settings?.ymalProductsWideArmrest) {
			Object.values(config.settings.settings.ymalProductsWideArmrest).forEach((ymalProducts) => {
				data.push(...ymalProducts.map((ymal) => `${String(ymal.type).toUpperCase()}-${ymal.id}`));
			});
		}
		if (config.settings?.settings?.ymalProductsLaptopTray) {
			Object.values(config.settings.settings.ymalProductsLaptopTray).forEach((ymalProducts) => {
				data.push(...ymalProducts.map((ymal) => `${String(ymal.type).toUpperCase()}-${ymal.id}`));
			});
		}
		if (config.settings?.settings?.ymalProductsHeadrest) {
			Object.values(config.settings.settings.ymalProductsHeadrest).forEach((ymalProducts) => {
				data.push(...ymalProducts.map((ymal) => `${String(ymal.type).toUpperCase()}-${ymal.id}`));
			});
		}
		const currentProducts = new Set([...data]);
		// Remove strange things like ART-leg or ART-001_mirror
		currentProducts.forEach((product) => {
			if (!product.match(/ART-\d+$|SPR-\d+$/gi)) {
				currentProducts.delete(product);
			}
		});
		return currentProducts;
	}

	private async getConfigData(range: string) {
		return await this.getCmsConfigData(range);
	}

	private async fetchJson(url: string): Promise<unknown> {
		if (this.username && this.password) {
			return await FetchService.json(url, {
				credentials: 'same-origin',
				headers: {
					accept: 'application/json',
					authorization:
						'Basic ' + Buffer.from(`${this.username}:${this.password}`).toString('base64'),
				},
			});
		}
		return await FetchService.json(url, {
			credentials: 'same-origin',
		});
	}
}
